/**
 * Safe access to a team's `team_admin` Keycloak group attribute.
 *
 * Teams created through the app always carry `team_admin`, because
 * KeycloakService.createGroup sets it at creation time. A group created any
 * other way does not: one added by hand in the Keycloak admin console, or a
 * SCIM-managed customer team, has no `attributes` object at all.
 *
 * A dozen render paths used to read `attributes.team_admin.includes(...)`
 * directly and threw "Cannot read properties of undefined", taking whole pages
 * down for anyone whose selected team came from outside the app. Treat a missing
 * list as "this team has no admins" — the same thing the already-guarded call
 * sites were doing individually.
 *
 * Parameters are `unknown` deliberately: callers pass ITeam, a raw Keycloak
 * group, or an element of cur_user.teams, and every one of them can be
 * undefined mid-load.
 */

/** Always an array, even when the attribute is absent. */
export const getTeamAdminEmails = (team: unknown): string[] => {
  const attributes = (team as { attributes?: unknown } | null | undefined)
    ?.attributes;
  const admins = (attributes as { team_admin?: unknown } | null | undefined)
    ?.team_admin;
  return Array.isArray(admins) ? (admins as string[]) : [];
};

/**
 * Whether `email` is listed as an admin of `team`. Comparison is exact, matching
 * the behaviour of the call sites this replaces — note the server normalizes
 * case when it resolves owners, so the two can disagree on a mixed-case address.
 */
export const isTeamAdmin = (team: unknown, email?: string | null): boolean =>
  Boolean(email) && getTeamAdminEmails(team).includes(email as string);

/** First listed admin, for the places that need a single owner email. */
export const getPrimaryTeamAdmin = (team: unknown): string | undefined =>
  getTeamAdminEmails(team)[0];
